import { useId, useRef, useState } from "react";
import { Text } from "@primer/react";
import "./EditablePercentage.css";

const percentageError = "Enter a percentage from 0 to 100 with up to two decimal places.";

export function EditablePercentage({ alignment = "end", allowEmpty = false, clickableUnit = false, disabled = false, editable = true, label, onCancel, onCommit, onInputChange, onSave, value }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(formatPercentage(value));
  const [startingValue, setStartingValue] = useState(value);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const committing = useRef(false);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);
  const keyboardEditing = useRef(false);
  const [restoredPointerFocus, setRestoredPointerFocus] = useState(false);
  const errorId = useId();

  function beginEditing() {
    keyboardEditing.current = document.documentElement.dataset.inputModality !== "pointer";
    setError(null);
    setStartingValue(value);
    setInput(formatPercentage(value));
    setEditing(true);
  }

  function closeEditor(restoreFocus) {
    const previousInput = inputRef.current;
    setRestoredPointerFocus(false);
    setEditing(false);
    if (restoreFocus) requestAnimationFrame(() => {
      if (buttonRef.current && (document.activeElement === document.body || document.activeElement === previousInput)) {
        setRestoredPointerFocus(!keyboardEditing.current);
        buttonRef.current.focus({ preventScroll: true });
      }
    });
  }

  function changeInput(nextInput) {
    setError(null);
    setInput(nextInput);
    const parsed = percentageToBasisPoints(nextInput, allowEmpty);
    onInputChange?.(nextInput, parsed, parsed === null ? percentageError : null);
  }

  async function commit() {
    if (committing.current) return;
    const parsed = percentageToBasisPoints(input, allowEmpty);
    if (onSave) {
      if (parsed === null) {
        setError(percentageError);
        return;
      }
      const restoreFocus = document.activeElement === inputRef.current;
      committing.current = true;
      setSaving(true);
      try {
        if (parsed !== value && !await onSave(parsed)) {
          setError("Not saved. Press Enter or leave the field to retry.");
          return;
        }
        closeEditor(restoreFocus);
      } catch (saveError) {
        setError(saveError.message);
      } finally {
        committing.current = false;
        setSaving(false);
      }
      return;
    }
    if (parsed === null) {
      setInput(formatPercentage(value));
      onInputChange?.(formatPercentage(value), value, null);
    } else {
      setInput(formatPercentage(parsed));
      onCommit(parsed);
    }
    setEditing(false);
  }

  function cancel() {
    if (committing.current) return;
    setError(null);
    setInput(formatPercentage(startingValue));
    onCancel?.(startingValue);
    onInputChange?.(formatPercentage(startingValue), startingValue, null);
    closeEditor(Boolean(onSave));
  }

  return <span className={`editable-percentage align-${alignment}${editing ? " editing" : ""}`} aria-busy={saving || undefined}>{editing ? <input ref={inputRef} className="percentage-token-input" aria-label={label} aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} readOnly={saving} inputMode="decimal" value={input} autoFocus onFocus={(event) => event.currentTarget.select()} onChange={(event) => changeInput(event.target.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); commit(); } if (event.key === "Escape") { event.preventDefault(); cancel(); } }} /> : editable ? <button ref={buttonRef} className="percentage-token" data-restored-pointer-focus={restoredPointerFocus || undefined} type="button" aria-label={label} disabled={disabled} onBlur={() => setRestoredPointerFocus(false)} onKeyDown={() => setRestoredPointerFocus(false)} onClick={beginEditing}>{formatPercentage(value)}{clickableUnit ? "%" : null}</button> : <Text className="percentage-token-view">{formatPercentage(value)}</Text>}{clickableUnit ? null : <Text className="percentage-unit" aria-hidden="true">%</Text>}{error ? <span className="percentage-error" id={errorId} role="alert"><span>{error}</span></span> : null}</span>;
}

function percentageToBasisPoints(value, allowEmpty = false) {
  const normalized = value.trim().replace(/%$/, "").trim();
  if (!normalized && allowEmpty) return 0;
  if (!/^(?:\d{1,2}(?:\.\d{0,2})?|100(?:\.0{0,2})?)$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

export function formatPercentage(basisPoints) {
  return (basisPoints / 100).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}
